/**
 * Confirm overlays for the firm detail panel — extracted from
 * firm-detail-panel.tsx.
 *
 * ConfirmOverlay is the single destructive-confirm modal behind clearing the
 * address, deleting a document, and clearing the three other text fields
 * (Notifications Email, Email Domains, Routing Preferences) — one chrome,
 * differing only in copy. DesignationConfirmOverlay stays separate — its body lists the
 * hide/show cascade and its confirm button is primary (not destructive) and
 * carries a pending state.
 */

import type { FirmDesignation } from "@command-center/shared";

/** Sections that show/hide per designation (ADR-0091 § Designation cascade). */
const DESIGNATION_AFFECTS: Record<FirmDesignation, string[]> = {
  Handling: ["Malpractice insurance", "MFA — Handling"],
  Originating: ["W-9", "MFA — Originating", "Address"],
  Both: [
    "Malpractice insurance",
    "MFA — Handling",
    "W-9",
    "MFA — Originating",
    "Address",
  ],
};

/** Sections present under `from` that are absent under `to`. */
function sectionsHidden(
  from: FirmDesignation | null,
  to: FirmDesignation,
): string[] {
  if (from === null) return [];
  const fromList = DESIGNATION_AFFECTS[from] ?? [];
  const toList = DESIGNATION_AFFECTS[to] ?? [];
  const next = new Set(toList);
  return fromList.filter((s) => !next.has(s));
}

/** Sections absent under `from` that appear under `to`. */
function sectionsShown(
  from: FirmDesignation | null,
  to: FirmDesignation,
): string[] {
  const toList = DESIGNATION_AFFECTS[to] ?? [];
  if (from === null) return toList;
  const fromList = DESIGNATION_AFFECTS[from] ?? [];
  const prev = new Set(fromList);
  return toList.filter((s) => !prev.has(s));
}

/** Destructive confirm modal. Centered over the panel; click-outside cancels,
 *  Escape cancels (handled in the parent's keydown listener). Configurable
 *  title / body / confirm label cover address-clear, document-delete, and the
 *  three other clearable text fields. */
export function ConfirmOverlay({
  title,
  description,
  confirmLabel,
  onCancel,
  onConfirm,
  testId,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  testId: string;
}) {
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-black/30"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid={`${testId}-overlay`}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        className="w-[320px] rounded-md border bg-card text-card-foreground p-4 shadow-lg"
        data-testid={testId}
      >
        <h4 id={`${testId}-title`} className="mb-2 text-sm font-semibold">
          {title}
        </h4>
        <p className="mb-3 text-xs text-muted-foreground">{description}</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-destructive px-3 py-1 text-xs font-medium text-destructive-foreground hover:bg-destructive/90"
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Confirm modal for Designation change. Lists the panel sections that will
 *  hide and show under the new value (ADR-0091 § Designation cascade).
 *  Reminds the operator that files are NEVER auto-deleted. */
export function DesignationConfirmOverlay({
  current,
  next,
  pending,
  onCancel,
  onConfirm,
  testId,
}: {
  current: FirmDesignation | null;
  next: FirmDesignation;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  testId: string;
}) {
  const hidden = sectionsHidden(current, next);
  const shown = sectionsShown(current, next);
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-black/30"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid={`${testId}-overlay`}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        className="w-[400px] rounded-md border bg-card text-card-foreground p-4 shadow-lg"
        data-testid={testId}
      >
        <h4 id={`${testId}-title`} className="mb-2 text-sm font-semibold">
          Change designation to {next}?
        </h4>
        {hidden.length > 0 ? (
          <div
            className="mb-2 text-xs"
            data-testid={`${testId}-hidden`}
          >
            <p className="mb-1 font-medium text-muted-foreground">
              Will hide:
            </p>
            <ul className="ml-4 list-disc text-muted-foreground">
              {hidden.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {shown.length > 0 ? (
          <div className="mb-2 text-xs" data-testid={`${testId}-shown`}>
            <p className="mb-1 font-medium text-muted-foreground">
              Will show:
            </p>
            <ul className="ml-4 list-disc text-muted-foreground">
              {shown.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="mb-3 text-xs italic text-muted-foreground">
          Files on the hidden slots stay in Airtable. They reappear if you flip
          designation back.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid={`${testId}-confirm`}
          >
            {pending ? "Saving…" : "Change"}
          </button>
        </div>
      </div>
    </div>
  );
}
